import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModuleCard } from './ModuleCard';
import { DemoForm } from './DemoForm';
import { DemoSuccess } from './DemoSuccess';
import { DemoRateLimited } from './DemoRateLimited';
import { DemoMobileCTA } from './DemoMobileCTA';
import { PreviewCard } from './PreviewCard';
import { DEMO_MODULES, type ModuleType } from '../../types/demo';

type DemoState = 'form' | 'success' | 'rate-limited';

export interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

interface DemoSectionProps {
  onContactClick: () => void;
}

export function DemoSection({ onContactClick }: DemoSectionProps) {
  const [selectedModule, setSelectedModule] = useState<ModuleType>('t12-financials');
  const [demoState, setDemoState] = useState<DemoState>('form');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState<{ retryAfter: string; message: string } | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo>({ firstName: '', lastName: '', email: '', company: '' });

  const handleSuccess = (email: string) => {
    setSubmittedEmail(email);
    setDemoState('success');
  };

  const handleRateLimited = (retryAfter: string, message: string) => {
    setRateLimitInfo({ retryAfter, message });
    setDemoState('rate-limited');
  };

  const handleReset = () => {
    setDemoState('form');
    setSubmittedEmail('');
    setRateLimitInfo(null);
  };

  return (
    <section id="demo" className="bg-gray-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header. Eyebrow badge dropped + H2 second-line italic
            "Try them now." removed (designer review: the badge was decor,
            and "Try It Now / Try them now" was language repeated twice). */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gsp-text-primary leading-tight">
            Three live modules.
          </h2>
          <p className="text-lg text-gsp-text-body mt-5 max-w-3xl mx-auto md:whitespace-nowrap md:max-w-none">
            Each one trained on the kinds of documents George Smith Partners handles every day.
          </p>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block">
          {/* Module Cards Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            {DEMO_MODULES.map((module) => (
              <ModuleCard
                key={module.id}
                id={module.id}
                title={module.title}
                description={module.description}
                icon={module.icon as 'BarChart3' | 'TrendingUp' | 'Building2'}
                selected={selectedModule === module.id}
                onClick={() => {
                  setSelectedModule(module.id);
                  // Reset to form state when switching modules
                  if (demoState !== 'form') {
                    handleReset();
                  }
                }}
              />
            ))}
          </div>

          {/* Form + Preview Side-by-Side Container */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${selectedModule}-${demoState}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {demoState === 'form' && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-stretch">
                    {/* Form - 60% */}
                    <div className="md:col-span-3">
                      <DemoForm
                        moduleType={selectedModule}
                        onSuccess={handleSuccess}
                        onRateLimited={handleRateLimited}
                        initialUserInfo={userInfo}
                        onUserInfoChange={setUserInfo}
                      />
                    </div>

                    {/* Sample Output Preview - 40% */}
                    <div className="md:col-span-2 flex">
                      <PreviewCard moduleType={selectedModule} />
                    </div>
                  </div>

                  {/* Terms of Service Notice - outside grid so it doesn't affect card heights */}
                  <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
                    Your use of this website and the services provided by this website are subject to and governed by the{' '}
                    <a
                      href="https://propvetter.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gsp-teal hover:underline"
                    >
                      Terms of Service
                    </a>
                    , and by using this website and the services provided you agree to comply with and be bound by such Terms of Service.
                  </p>
                </div>
              )}

              {demoState === 'success' && (
                <DemoSuccess
                  email={submittedEmail}
                  onReset={handleReset}
                />
              )}

              {demoState === 'rate-limited' && rateLimitInfo && (
                <DemoRateLimited
                  message={rateLimitInfo.message}
                  retryAfter={rateLimitInfo.retryAfter}
                  onContactClick={onContactClick}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Module Cards - Stacked */}
          <div className="space-y-4 mb-8">
            {DEMO_MODULES.map((module) => (
              <div
                key={module.id}
                className="p-4 bg-white rounded-xl border border-gray-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gsp-teal/10 flex items-center justify-center flex-shrink-0">
                    {module.icon === 'BarChart3' && (
                      <svg className="w-5 h-5 text-gsp-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    )}
                    {module.icon === 'TrendingUp' && (
                      <svg className="w-5 h-5 text-gsp-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    )}
                    {module.icon === 'Building2' && (
                      <svg className="w-5 h-5 text-gsp-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gsp-text-primary">
                      {module.title}
                    </h3>
                    <p className="text-sm text-gsp-text-body mt-1">
                      {module.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile CTA */}
          <DemoMobileCTA onContactClick={onContactClick} />
        </div>
      </div>
    </section>
  );
}

export default DemoSection;
